/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @next/next/no-img-element */
"use client";

import Navbar from "@/shared/Navbar";
import { motion } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { Lightbulb } from "lucide-react";
import { usePages } from "@/hooks/usePages";
import Asklocal from "@/components/asklocalText/asklocal";
import { IoIosPlayCircle } from "react-icons/io";
import AgentDifferentiationBlock from "./buy-sell/AgentDifferentiationBlock";
// import CalendlySection from "./buy-sell/GrowthCalendlySection";
import AgentPainBlock from "./buy-sell/AgentPainBlock";
import BecomeAskLocalPartnerForm from "./buy-sell/BecomeAskLocalPartnerFrom";
import VideoModal from "./buy-sell/VideoModal";
import GuideTrustSection from "./buy-sell/GuideTrustSection";

const slides = [
  {
    image: "/slides/step-1.jpeg",
    title: "See the Buyer Experience Your Clients Deserve.",

    subtitle:
      "Step inside AskLocal and experience the complete home-buying journey exactly as your clients will - from first questions to closing.",
  },
  {
    image: "/slides/step-2.jpeg",
    title: "Deliver a Smarter Selling Experience",

    subtitle:
      "Coordinate timelines, services, and communication in one transparent roadmap that keeps sellers informed and confident.",
  },
  // newly updated
  {
    image: "/slides/slide-3.png",
    title: "Stop Chasing Your Deals",

    subtitle:
      "Centralize your entire transaction and look like a pro - without doing more work.",
  },
];

export default function Page() {
  const [index, setIndex] = useState(0);
  const [openPartner, setOpenPartner] = useState(false);
  const { data: pages, loading } = usePages();

  const [openVideo, setOpenVideo] = useState(false);
  useEffect(() => {
    const handleOpenPartner = () => {
      setOpenPartner(true);
    };

    window.addEventListener("openAskLocalPartner", handleOpenPartner);

    return () => {
      window.removeEventListener("openAskLocalPartner", handleOpenPartner);
    };
  }, []);

  // 🔁 Auto slide
  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  // ✅ Combine dynamic + static pages for dropdown
  const howToHubDropdown = useMemo(() => {
    const staticPages = [
      {
        label: "Property Intelligence Report",
        href: "/how-to-hub/property-intelligence-report",
      },

      { label: "Mortgage Guide", href: "/how-to-hub/mortgage-guide" },
    ];

    const dynamicPages =
      pages?.map((p: any) => ({
        label: p.title || "Untitled",
        href: `/how-to-hub/${p.slug}`,
      })) || [];

    return [
      ...(dynamicPages.length
        ? dynamicPages
        : loading
          ? [{ label: "Loading...", href: "#" }]
          : []),
      ...staticPages,
    ];
  }, [pages, loading]);

  return (
    <div className="flex flex-col text-white">
      {/* 🔹 Navbar */}
      <div className="relative overflow-hidden">
        <Navbar
          defaultLogo="/logo/asklocal-white-logo.png"
          scrolledLogo="/logo/color-full-logo.png"
          isLight
          labelColor="#ffffff"
          labelHoverColor="#EB9C2C"
          buttonBg="#EB9C2C"
          buttonTextColor="#FFFFFF"
          buttonHoverBg="#4177BC"
          buttonHoverText="#ffffff"
          ctaText="Looking to Buy/Sell?"
          ctaLink="/buy-sell"
          navItems={[
            { label: "Home", href: "/" },

            {
              label: "Property Reports",
              href: "/how-to-hub/property-intelligence-report",
            },
            {
              label: "Search for Homes",
              href: "https://www.asklocalhomes.com/",
            },
            {
              label: "How-To-Hub",
              dropdown: howToHubDropdown,
            },
            { label: "Log In", href: "https://portal.asklocal.com/" },
          ]}
        />

        {/* 🔹 Hero Slider */}
        <div className="relative min-h-[85vh] sm:min-h-[80vh] w-full overflow-hidden">
          {slides.map((slide, i) => (
            <motion.div
              key={i}
              className="absolute inset-0"
              animate={{
                opacity: index === i ? 1 : 0,
                scale: index === i ? 1 : 1.03,
              }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-linear-to-b from-black/60 via-black/30 to-black/80"></div>
            </motion.div>
          ))}

          {/* Text */}
          <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-6 judson-regular">
            <motion.h1
              key={slides[index].title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-3xl sm:text-5xl lg:text-5xl inter-semibold max-w-4xl mx-auto leading-tight drop-shadow-lg"
            >
              {slides[index].title}
            </motion.h1>

            <motion.p
              key={slides[index].subtitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-white/95 text-lg sm:text-xl lg:text-2xl mt-6 max-w-[720px] judson-regular"
            >
              {slides[index].subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="mt-10 flex flex-wrap justify-center gap-4"
            >
              {/* Partner Button */}
              <button
                onClick={() => setOpenPartner(true)}
                className="bg-[#EB9C2C] hover:bg-white hover:text-[#4177BC] text-white inter-semibold px-[13px] py-3 rounded-full transition-all duration-300 shadow-md"
              >
                Register to Experience the Buyer Journey
              </button>

              {/* Video Button */}
              <button
                onClick={() => setOpenVideo(true)}
                onMouseEnter={() => setOpenVideo(false)} // optional hover logic
                className="bg-white/10 backdrop-blur-md border flex items-center border-white/30 hover:bg-white hover:text-[#4177BC] text-white inter-semibold px-7 py-3 rounded-full transition-all duration-300"
              >
                <IoIosPlayCircle className="text-4xl" />
                See How AskLocal Works
              </button>
              {/* micro text */}
            </motion.div>
            <motion.p
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-sm inter-medium text-white/80 text-center py-4"
            >
              Free registration. Experience the platform exactly as your buyers
              would.
            </motion.p>
          </div>

          {/* Dots */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${
                  index === i ? "bg-[#EB9C2C] w-6" : "bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 🔹 Floating Tip Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
        className="relative z-20 -mt-20 bg-white text-[#111827] shadow-xl px-6 sm:px-10 py-8 rounded-2xl w-[90%] sm:w-[600px] mx-auto border border-gray-100"
      >
        <div className="flex items-center justify-center gap-2 text-gray-700">
          <Lightbulb className="w-5 h-5 text-[#EB9C2C]" />
          <p className="text-sm sm:text-base font-medium text-center">
            Tip: Brokerages use AskLocal to standardize client guidance,
            document referrals, and protect agents with a compliant,
            concierge-led experience without changing how you do business.
            AskLocal operates under SOC 2 Type II controls and maintains a
            public SOC 3 report to support secure, industry-standard data
            handling.
          </p>
        </div>
      </motion.div>
      <section>
        <AgentPainBlock />
      </section>
      {/* 🔹 Partner Benefits Section */}

      <GuideTrustSection />

      <AgentDifferentiationBlock />
      {/* 🔹 Growth Calendly Section */}
      {/* <section className=" text-[#111827] text-center ">
        <CalendlySection />
      </section> */}

      <section>
        <Asklocal />
      </section>

      {/* 🔹 Modal */}
      <BecomeAskLocalPartnerForm
        isOpen={openPartner}
        onClose={() => setOpenPartner(false)}
      />
      <VideoModal
        isOpen={openVideo}
        onClose={() => setOpenVideo(false)}
        src="/asklocal-agent/HowItWorks.mp4"
      />
    </div>
  );
}
